import React, { useState } from 'react'
import {
  Container,
  Breadcrumb,
  Row,
  Col,
  Table,
  Button,
  Card,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Badge,
} from 'react-bootstrap'
import { Link, useParams, useHistory } from 'react-router-dom'
import useTerminalDetail from 'hooks/use-terminal-detail'
import useAnsibleDetail from 'hooks/use-ansible-detail'
import useMacMiniDetail from 'hooks/use-mac-mini-detail'
import usePhysicalPhoneDetail from 'hooks/use-physical-phone-detail'
import useMacMiniUsers from 'hooks/use-mac-mini-users'
import useMacMiniUserDetail from 'hooks/use-mac-mini-user-detail'
import {
  PnsMacMiniUser,
  PnsMacMiniUserStatus,
  WispStatus,
} from 'interfaces/pns-mac-mini-user'
import useDebounce from 'hooks/use-debounce'
import { MacMiniUserStatusUpdateModal } from 'components/mac-mini-user-status'
import AppleIdConfigCard from 'components/apple-id-config-card'

const InventoryMacMiniUsersPage = () => {
  const { terminalId, ansibleId, macMiniId, phoneId, userId } = useParams<{
    terminalId: string
    ansibleId: string
    macMiniId?: string
    phoneId?: string
    userId?: string
  }>()
  const history = useHistory()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [wispStatusFilter, setWispStatusFilter] = useState('')
  const [page] = useState(1)
  const [showStatusUpdateModal, setShowStatusUpdateModal] = useState(false)

  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  // Fetch related data
  const { data: terminal } = useTerminalDetail(terminalId)
  const { data: ansible } = useAnsibleDetail(ansibleId)
  const { data: macMini } = useMacMiniDetail(macMiniId)
  const { data: phone } = usePhysicalPhoneDetail(phoneId)

  // Fetch single user detail if userId is provided
  const {
    data: singleUserData,
    isLoading: singleUserLoading,
    error: singleUserError,
  } = useMacMiniUserDetail(userId || '')

  // Determine the appropriate filter for users list (only when not viewing single user)
  const userFilters = !userId
    ? macMiniId
      ? { macMiniId: macMiniId }
      : phoneId && phone?.macMiniUser?.macMiniId
      ? { macMiniId: phone.macMiniUser.macMiniId }
      : {}
    : {}

  const {
    data: usersData,
    isLoading: usersLoading,
    error: usersError,
  } = useMacMiniUsers(
    {
      ...userFilters,
      page,
      limit: 20,
      usernameSearch: debouncedSearchTerm,
      status: statusFilter,
      wispStatus: wispStatusFilter,
      include: 'all',
    },
    !userId
  ) // Only enabled when not viewing a single user

  const getStatusVariant = (status: PnsMacMiniUserStatus) => {
    switch (status) {
      case PnsMacMiniUserStatus.ACTIVE:
        return 'success'
      case PnsMacMiniUserStatus.AVAILABLE:
        return 'info'
      case PnsMacMiniUserStatus.RESERVED:
        return 'warning'
      case PnsMacMiniUserStatus.ICLOUD_SETUP_IN_PROGRESS:
        return 'info'
      case PnsMacMiniUserStatus.INACTIVE:
        return 'secondary'
      case PnsMacMiniUserStatus.TERMINATED:
        return 'danger'
      default:
        return 'secondary'
    }
  }

  const getWispStatusVariant = (status: WispStatus) => {
    switch (status) {
      case WispStatus.RUNNING:
        return 'success'
      case WispStatus.STARTING:
        return 'warning'
      case WispStatus.STOPPED:
        return 'danger'
      default:
        return 'secondary'
    }
  }

  // Determine the device name for breadcrumb
  const deviceName = macMini?.name || phone?.name || 'Device'

  // If showing a specific user, use the single user data
  const currentUser = userId ? singleUserData : null
  const isLoading = userId ? singleUserLoading : usersLoading
  const error = userId ? singleUserError : usersError

  if (error) {
    return (
      <Container>
        <Alert variant="danger">
          Error loading Mac Mini {userId ? 'user' : 'users'}:{' '}
          {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
      </Container>
    )
  }

  return (
    <Container>
      <Breadcrumb>
        <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
        <Breadcrumb.Item href="/inventory">Inventory</Breadcrumb.Item>
        <Breadcrumb.Item href={`/inventory/terminals/${terminalId}`}>
          {terminal?.name || 'Terminal'}
        </Breadcrumb.Item>
        <Breadcrumb.Item
          href={`/inventory/terminals/${terminalId}/ansibles/${ansibleId}`}
        >
          {ansible?.name || 'Ansible'}
        </Breadcrumb.Item>
        <Breadcrumb.Item
          href={
            macMiniId
              ? `/inventory/terminals/${terminalId}/ansibles/${ansibleId}/mac-mini/${macMiniId}`
              : `/inventory/terminals/${terminalId}/ansibles/${ansibleId}/phone/${phoneId}`
          }
        >
          {deviceName}
        </Breadcrumb.Item>
        {currentUser ? (
          <Breadcrumb.Item active>{currentUser.username}</Breadcrumb.Item>
        ) : (
          <Breadcrumb.Item active>Users</Breadcrumb.Item>
        )}
      </Breadcrumb>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>
          {currentUser
            ? `Mac Mini User: ${currentUser.username}`
            : 'Mac Mini Users'}
        </h1>
        {currentUser && (
          <div className="d-flex align-items-center gap-2">
            <Badge
              variant={getStatusVariant(currentUser.status)}
              className="fs-6"
            >
              {currentUser.status}
            </Badge>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowStatusUpdateModal(true)}
            >
              Change Status
            </Button>
          </div>
        )}
      </div>

      {currentUser ? (
        <>
          {/* Single user detail view */}
          <Card className="mb-4">
            <Card.Header>
              <h4>Mac Mini User Details</h4>
            </Card.Header>
            <Card.Body>
              {isLoading ? (
                <div className="text-center">
                  <Spinner animation="border" />
                  <div className="mt-2">Loading user details...</div>
                </div>
              ) : (
                <Row>
                  <Col md={6}>
                    <p>
                      <strong>Username:</strong> {currentUser.username}
                    </p>
                    <p>
                      <strong>Status:</strong>
                      <Badge
                        variant={getStatusVariant(currentUser.status)}
                        className="ms-2"
                      >
                        {currentUser.status}
                      </Badge>
                    </p>
                    <p>
                      <strong>WISP Status:</strong>
                      <Badge
                        variant={getWispStatusVariant(currentUser.wispStatus)}
                        className="ms-2"
                      >
                        {currentUser.wispStatus}
                      </Badge>
                    </p>
                    <p>
                      <strong>WISP Port:</strong> {currentUser.wispPort}
                    </p>
                  </Col>
                  <Col md={6}>
                    <p>
                      <strong>Service FQDN:</strong>{' '}
                      {currentUser.serviceFqdn || 'N/A'}
                    </p>
                    <p>
                      <strong>Mac Mini:</strong>{' '}
                      {currentUser.macMini?.name || 'N/A'}
                    </p>
                    <p>
                      <strong>Last Activity:</strong>{' '}
                      {currentUser.lastActivity
                        ? new Date(currentUser.lastActivity).toLocaleString()
                        : 'N/A'}
                    </p>
                    <p>
                      <strong>Created:</strong>{' '}
                      {new Date(currentUser.createdAt).toLocaleString()}
                    </p>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>

          {/* Apple ID Card */}
          <AppleIdConfigCard appleIdConfig={currentUser.appleIdConfig} />
        </>
      ) : (
        // Multiple users table view
        <Card className="mb-4">
          <Card.Header>
            <h4>User Management</h4>
          </Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={3}>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Search username..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <Form.Control
                  as="select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="ACTIVE">Active</option>
                  <option value="AVAILABLE">Available</option>
                  <option value="RESERVED">Reserved</option>
                  <option value="ICLOUD_SETUP_IN_PROGRESS">iCloud Setup</option>
                  <option value="INACTIVE">Inactive</option>
                  <option value="TERMINATED">Terminated</option>
                </Form.Control>
              </Col>
              <Col md={3}>
                <Form.Control
                  as="select"
                  value={wispStatusFilter}
                  onChange={(e) => setWispStatusFilter(e.target.value)}
                >
                  <option value="">All WISP Statuses</option>
                  <option value="RUNNING">Running</option>
                  <option value="STARTING">Starting</option>
                  <option value="STOPPED">Stopped</option>
                </Form.Control>
              </Col>
            </Row>

            {isLoading ? (
              <div className="text-center">
                <Spinner animation="border" />
                <div className="mt-2">Loading users...</div>
              </div>
            ) : (
              <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                <Table striped bordered hover responsive variant="dark">
                  <thead
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 100,
                      backgroundColor: '#343a40',
                    }}
                  >
                    <tr>
                      <th style={{ borderTop: 'none', width: '80px' }}>#</th>
                      <th style={{ borderTop: 'none', width: '150px' }}>
                        Username
                      </th>
                      <th style={{ borderTop: 'none', width: '100px' }}>
                        Status
                      </th>
                      <th style={{ borderTop: 'none', width: '120px' }}>
                        WISP Status
                      </th>
                      <th style={{ borderTop: 'none', width: '100px' }}>
                        WISP Port
                      </th>
                      <th style={{ borderTop: 'none', width: '200px' }}>
                        Service FQDN
                      </th>
                      <th style={{ borderTop: 'none', width: '150px' }}>
                        Last Activity
                      </th>
                      <th style={{ borderTop: 'none', width: '120px' }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersData?.data?.map((user, index) => (
                      <tr key={user.id}>
                        <td>{(page - 1) * 20 + index + 1}</td>
                        <td>
                          <Link
                            to={
                              macMiniId
                                ? `/inventory/terminals/${terminalId}/ansibles/${ansibleId}/mac-mini/${macMiniId}/users/${user.id}`
                                : `/inventory/terminals/${terminalId}/ansibles/${ansibleId}/phone/${phoneId}/users/${user.id}`
                            }
                            style={{
                              color: '#4da6ff',
                              textDecoration: 'underline',
                            }}
                          >
                            {user.username}
                          </Link>
                        </td>
                        <td>
                          <Badge variant={getStatusVariant(user.status)}>
                            {user.status}
                          </Badge>
                        </td>
                        <td>
                          <Badge
                            variant={getWispStatusVariant(user.wispStatus)}
                          >
                            {user.wispStatus}
                          </Badge>
                        </td>
                        <td>{user.wispPort}</td>
                        <td>{user.serviceFqdn || 'N/A'}</td>
                        <td>
                          {user.lastActivity
                            ? new Date(user.lastActivity).toLocaleDateString()
                            : 'N/A'}
                        </td>
                        <td>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() =>
                              history.push(
                                macMiniId
                                  ? `/inventory/terminals/${terminalId}/ansibles/${ansibleId}/mac-mini/${macMiniId}/users/${user.id}`
                                  : `/inventory/terminals/${terminalId}/ansibles/${ansibleId}/phone/${phoneId}/users/${user.id}`
                              )
                            }
                          >
                            View Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {usersData?.data?.length === 0 && (
              <div className="text-center text-muted py-4">
                No users found matching your criteria
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Mac Mini User Status Update Modal */}
      {currentUser && (
        <MacMiniUserStatusUpdateModal
          show={showStatusUpdateModal}
          onHide={() => setShowStatusUpdateModal(false)}
          macMiniUser={currentUser}
          onSuccess={() => {
            setShowStatusUpdateModal(false)
            // Refresh the page to show updated status
            window.location.reload()
          }}
        />
      )}
    </Container>
  )
}

export default InventoryMacMiniUsersPage
